/* eslint-disable camelcase */
import type { CamelCasedPropertiesDeep } from 'type-fest';
import type { ApplicationType } from '..';

type DeductionApplicationScope = 'PRODUCT_SCOPE' | 'SUBSCRIPTION_SCOPE';

interface Entity {
  entity_identifier: string;
  entity_type: string;
  entity_label: string;
}

interface ComponentCostsDto {
  component_identifier: string;
  amount_with_tax: number;
  amount_without_tax: number;
  tax_amount: number;
  tax_rate?: number;
}

interface PurchasedProductCostsDto {
  entity: Entity;
  amount_with_tax: number;
  amount_without_tax: number;
  tax_amount: number;
  tax_rate?: number;
  component_costs: ComponentCostsDto[];
}

interface ComponentsDeductionDto {
  identifier: string;
  application_type: ApplicationType;
  application_scope: DeductionApplicationScope;
  percentage?: number;
  amount?: number;
}

interface PurchasedProductDto {
  sku: string;
  components_fixed_volume: Array<{
    identifier: string;
    quantity: number;
  }>;
  components_selectable_volume: Array<{
    identifier: string;
    available_quantities: number[];
    quantity: number;
  }>;
  components_percentage_deduction: ComponentsDeductionDto[];
  components_monetary_deduction: ComponentsDeductionDto[];
  entity: Entity;
}

interface SubscriptionDto {
  billing_period: 'MONTHLY' | 'YEARLY';
  created?: string;
  start_date: string;
  current_period_start_date: string;
  current_period_end_date: string;
  products: PurchasedProductDto[];
  status: string;
}

export enum PaymentMethodType {
  CREDIT_CARD = 'CREDIT_CARD',
  PAYPAL = 'PAYPAL',
}

interface CreditCardDto {
  card_number: string;
  expiration_month: number;
  expiration_year: number;
  credit_card_type: 'VISA' | 'MASTERCARD' | 'AMERICAN_EXPRESS' | 'DISCOVER' | 'JCB' | 'DINERS';
}

interface PaypalDto {
  id: string;
}

type PaymentMethodDto =
  | {
      payment_method_type: PaymentMethodType.CREDIT_CARD;
      details: CreditCardDto;
    }
  | {
      payment_method_type: PaymentMethodType.PAYPAL;
      details: PaypalDto;
    };

export interface CustomerDto {
  identifier: number;
  hotjar_account_id: number;
  created_at: string;
  company_name: string;
  billing_address: {
    address_line1: string | null;
    address_line2: string | null;
    city: string | null;
    zip_code: string | null;
    country: string | null;
    state: string | null;
    county: string;
    first_name: string;
    last_name: string;
    email_address: string;
  };
  additional_email_addresses: string[];
  tax_number: string;
  currency: 'EUR' | 'USD';
  external_customer: {
    external_id: string;
    payment_method_external_id: string;
    external_url: string;
    provider: string;
  };
  subscription: SubscriptionDto;
  current_period_cost_summary: {
    amount_with_tax: number;
    amount_without_tax: number;
    tax_amount: number;
    purchased_product_costs: PurchasedProductCostsDto[];
    tax_rate: number;
  };
  payment_method?: PaymentMethodDto;
}

export type ComponentsDeduction = CamelCasedPropertiesDeep<ComponentsDeductionDto>;

export type CustomerPurchasedProductCosts = CamelCasedPropertiesDeep<PurchasedProductCostsDto>;

export type CustomerCreditCard = CamelCasedPropertiesDeep<CreditCardDto>;

export type CustomerPaypal = CamelCasedPropertiesDeep<PaypalDto>;

export type CustomerPaymentMethod = CamelCasedPropertiesDeep<PaymentMethodDto>;

export type Customer = CamelCasedPropertiesDeep<CustomerDto>;
